"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, onValue, update, set } from "firebase/database"
import "./userMachineryOrders.css"
import InfoIcon from "./infoIcon"

const MachineryOrders = ({ adminView = false, selectedBrand = "" }) => {
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [replyDialogOpen, setReplyDialogOpen] = useState(false)
  const [adminReply, setAdminReply] = useState("")
  const [replyStatus, setReplyStatus] = useState("Processing")
  const [refreshTrigger, setRefreshTrigger] = useState(0)
   const gifUrl = "/Images/New notification.gif"
   const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1") // insert space before uppercase letters
    .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter


  // Fetch orders from Firebase
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true)
      try {
        const db = getDatabase()
        const ordersRef = ref(db, "machineryOrder")

        onValue(ordersRef, (snapshot) => {
          if (snapshot.exists()) {
            const ordersData = snapshot.val()
            const ordersArray = []

            // Convert the nested structure to a flat array
            Object.keys(ordersData).forEach((userId) => {
              Object.keys(ordersData[userId]).forEach((orderId) => {
                ordersArray.push({
                  id: orderId,
                  userId,
                  ...ordersData[userId][orderId],
                })
              })
            })

            // Sort by timestamp (newest first)
            ordersArray.sort((a, b) => {
              return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            })

            setOrders(ordersArray)
          } else {
            setOrders([])
          }
          setLoading(false)
        })
      } catch (error) {
        console.error("Error fetching orders:", error)
        setLoading(false)
      }
    }

    fetchOrders()
  }, [refreshTrigger])

  // Filter orders based on search term, status, and selected brand
  useEffect(() => {
    let filtered = [...orders]

    // Filter by brand if in admin view and a brand is selected
    if (adminView && selectedBrand) {
      filtered = filtered.filter((order) => order.brand === selectedBrand)
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((order) => order.status === statusFilter)
    }

    // Filter by search term (company name, order ID, or machinery type)
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (order) =>
          order.companyName?.toLowerCase().includes(term) ||
          order.id.toLowerCase().includes(term) ||
          order.machinery?.toLowerCase().includes(term),
      )
    }

    setFilteredOrders(filtered)
  }, [orders, searchTerm, statusFilter, adminView, selectedBrand])

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const handleReplySubmit = async () => {
    if (!selectedOrder || !adminReply.trim()) return

    try {
      const db = getDatabase()
      const orderRef = ref(db, `machineryOrder/${selectedOrder.userId}/${selectedOrder.id}`)

      // Get current Indian timestamp
      const now = new Date()
      const istOffset = 5.5 * 60 * 60 * 1000 // IST is UTC+5:30
      const istTime = new Date(now.getTime() + istOffset)
      const timestamp = istTime.toISOString().replace("T", " ").replace("Z", "")

      // Update the order with admin reply and new status
      await update(orderRef, {
        adminReply,
        adminReplyTimestamp: timestamp,
        status: replyStatus,
      })

      // Send notification to user
      const notificationRef = ref(db, `user_notifications/machinery orders/${selectedOrder.userId}/${Date.now()}`)
      await set(notificationRef, {
        title: "Machinery Order Update",
        gifUrl,
        message: `Your machinery order (${selectedOrder.machinery}) has been ${replyStatus.toLowerCase()}.`,
        timestamp,
        seen: false,
        type: "order",
        orderId: selectedOrder.id,
      })

      // Close dialog and refresh orders
      setReplyDialogOpen(false)
      setAdminReply("")
      setReplyStatus("Processing")
      handleRefresh()
    } catch (error) {
      console.error("Error updating order:", error)
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="status-badge pending">Pending</span>
      case "Processing":
        return <span className="status-badge processing">Processing</span>
      case "Completed":
        return <span className="status-badge completed">Completed</span>
      case "Cancelled":
        return <span className="status-badge cancelled">Cancelled</span>
      default:
        return <span className="status-badge">{status}</span>
    }
  }
  return (
    <div className="machinery-orders-container">
      <div className="machinery-orders-header">
        <h2>Machinery Orders <InfoIcon text="The Machinery Orders page provides a detailed overview of all equipment procurement and delivery activities for the company. Each order is listed with the Company responsible, the specific Machinery being supplied, the Site Address where the machinery will be delivered, and the scheduled Delivery Date. The Status column clearly indicates whether the machinery is pending, dispatched, or delivered, allowing teams to track progress in real time. Additionally, the Actions section enables users to update order details, confirm receipt, or manage logistics efficiently. This centralized view ensures that all machinery orders are monitored effectively, delays are minimized, and construction or production schedules stay on track." /></h2>
        <p>Manage machinery orders from customers</p>
        <button className="refresh-button" onClick={handleRefresh}>
          <i className="refresh-icon">↻</i>
        </button>
      </div>

      <div className="machinery-orders-filters">
        <div className="status-tabs">
          <button
            className={`tab-button ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All
          </button>
          <button
            className={`tab-button ${statusFilter === "Pending" ? "active" : ""}`}
            onClick={() => setStatusFilter("Pending")}
          >
            Pending
          </button>
          <button
            className={`tab-button ${statusFilter === "Processing" ? "active" : ""}`}
            onClick={() => setStatusFilter("Processing")}
          >
            Processing
          </button>
          <button
            className={`tab-button ${statusFilter === "Completed" ? "active" : ""}`}
            onClick={() => setStatusFilter("Completed")}
          >
            Completed
          </button>
        </div>

        <div className="search-container">
          <input
            type="search"
            placeholder="Search by company or machinery..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <i className="search-icon">🔍</i>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <p>Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-container">
          <p>No machinery orders found.</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Machinery</th>
                <th>Site Address</th>
                <th>Delivery Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="company-cell">{order.companyName}</td>
                  <td>
                    <div className="machinery-details">
                      <span className="brand">{order.brand}</span>
                      <span className="type">{order.machinery}</span>
                    </div>
                  </td>
                  <td>
                    <div className="site-address" title={order.siteAddress}>
                      {order.siteAddress}
                    </div>
                  </td>
                  <td>{order.deliverydate}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>
                    <button
                      className="reply-button"
                      onClick={() => {
                        setSelectedOrder(order)
                        setReplyDialogOpen(true)
                        setAdminReply(order.adminReply || "")
                        setReplyStatus(order.status)
                      }}
                    >
                      {order.adminReply ? "View/Edit Reply" : "Reply"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reply Dialog */}
{/* Reply Dialog */}
{replyDialogOpen && (
  <div className="replyOrder-overlay">
    <div className="replyOrder-modal">
      <div className="replyOrder-header">
        <h3>{selectedOrder?.adminReply ? "Update Reply" : "Reply to Order"}</h3>
        <button
          className="replyOrder-closeBtn"
          onClick={() => setReplyDialogOpen(false)}
        >
          ×
        </button>
      </div>

      {selectedOrder && (
        <div className="replyOrder-content">
          <div className="replyOrder-grid">
            <div className="replyOrder-detail">
              <h4>Company</h4>
              <p>
                {selectedOrder.companyName || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
            <div className="replyOrder-detail">
              <h4>Category</h4>
              <p>
                {selectedOrder.category || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
          </div>

          <div className="replyOrder-grid">
            <div className="replyOrder-detail">
              <h4>Machinery</h4>
              <p>
                {selectedOrder.brand || "Unknown Brand"} -{" "}
                {selectedOrder.machinery || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
            <div className="replyOrder-detail">
              <h4>Condition</h4>
              <p>
                {selectedOrder.condition || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
          </div>

          <div className="replyOrder-grid">
            <div className="replyOrder-detail">
              <h4>Payment Option</h4>
              <p>
                {selectedOrder.paymentOption || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
            <div className="replyOrder-detail">
              <h4>Delivery Date</h4>
              <p>
                {selectedOrder.deliverydate || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
          </div>

          <div className="replyOrder-grid">
            <div className="replyOrder-detail">
              <h4>Contact Person</h4>
              <p>
                {selectedOrder.contactPersonName || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
            <div className="replyOrder-detail">
              <h4>Phone</h4>
              <p>
                {selectedOrder.contactPhoneNumber || (
                  <span className="replyOrder-missing">Missing</span>
                )}
              </p>
            </div>
          </div>

          <div className="replyOrder-detail full">
            <h4>Email</h4>
            <p>
              {selectedOrder.contactEmail || (
                <span className="replyOrder-missing">Missing</span>
              )}
            </p>
          </div>

          <div className="replyOrder-detail full">
            <h4>Site Address</h4>
            <p>
              {selectedOrder.siteAddress || (
                <span className="replyOrder-missing">Missing</span>
              )}
            </p>
          </div>

          {selectedOrder.specifications ? (
            <div className="replyOrder-detail full">
              <h4>Specifications</h4>
              <ul className="replyOrder-specs">
                {(() => {
                  try {
                    const specs =
                      typeof selectedOrder.specifications === "string"
                        ? JSON.parse(selectedOrder.specifications)
                        : selectedOrder.specifications;
                    return Object.entries(specs).map(([key, value]) => (
                      <li key={key}>
                        <strong>{formatKey(key)}:</strong> {value}
                      </li>
                    ));
                  } catch (err) {
                    return <li>{selectedOrder.specifications}</li>;
                  }
                })()}
              </ul>
            </div>
          ) : (
            <div className="replyOrder-detail full">
              <h4>Specifications</h4>
              <p>
                <span className="replyOrder-missing">Not Provided</span>
              </p>
            </div>
          )}

          <div className="replyOrder-detail full">
            <h4>Order Timestamp</h4>
            <p>
              {selectedOrder.timestamp || (
                <span className="replyOrder-missing">Missing</span>
              )}
            </p>
          </div>

          <div className="replyOrder-formGroup">
            <label htmlFor="status">Order Status</label>
            <select
              id="status"
              value={replyStatus}
              onChange={(e) => setReplyStatus(e.target.value)}
              className="replyOrder-select"
            >
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="replyOrder-formGroup">
            <label htmlFor="reply">Reply Message</label>
            <textarea
              id="reply"
              placeholder="Enter your reply to the customer..."
              rows={5}
              value={adminReply}
              onChange={(e) => setAdminReply(e.target.value)}
              className="replyOrder-textarea"
            />
          </div>
        </div>
      )}

      <div className="replyOrder-footer">
        <button className="replyOrder-submitBtn" onClick={handleReplySubmit}>
          <span className="replyOrder-sendIcon">📤</span>
          {selectedOrder?.adminReply ? "Update Reply" : "Send Reply"}
        </button>
        <button
          className="replyOrder-cancelBtn"
          onClick={() => setReplyDialogOpen(false)}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}


    </div>
  )
}

export default MachineryOrders;
